import { Subscription } from 'rxjs';
import * as THREE from 'three';
import { Npc } from '../character/Npc';
import { Car } from '../vehicles/Car';
import { World } from './World';
import { WantedLevelService } from './WantedLevelService';
import { NpcSpawnService } from './NpcSpawnService';
import { CopSpawnPoint } from './CopSpawnPoint';
import { CopCarSpawnPoint } from './CopCarSpawnPoint';

interface SpawnTableEntry {
    footCops: number;
    copCars: number;
}

const SPAWN_TABLE: Record<number, SpawnTableEntry> = {
    0: { footCops: 0, copCars: 0 },
    1: { footCops: 1, copCars: 0 },
    2: { footCops: 2, copCars: 1 },
    3: { footCops: 3, copCars: 2 },
    4: { footCops: 4, copCars: 3 },
    5: { footCops: 5, copCars: 4 },
};

const MAX_ACTIVE_UNITS = 10;
const OFFSCREEN_SPAWN_RADIUS = 30;

export class CopSpawnService {
    public readonly activeUnits: Set<Npc> = new Set();
    public readonly activeCopCars: Set<Car> = new Set();
    private readonly subscription: Subscription;

    constructor(
        private readonly world: World,
        wantedLevelService: WantedLevelService,
        private readonly npcSpawnService: NpcSpawnService
    ) {
        this.subscription = wantedLevelService.wantedLevel$.subscribe((level) => {
            this.onWantedLevelChange(level);
        });
    }

    get anyPursuerHasLoS(): boolean {
        for (const npc of this.activeUnits) {
            if ((npc as any).behaviour?.hasLoS === true) { return true; }
        }
        return false;
    }

    public targetFootCopCount(level: number): number {
        return SPAWN_TABLE[level]?.footCops ?? 0;
    }

    public targetCopCarCount(level: number): number {
        return SPAWN_TABLE[level]?.copCars ?? 0;
    }

    public spawnSingleCopNearPlayer(): void {
        if (!this.world.player) { return; }
        if (this.activeUnits.size >= MAX_ACTIVE_UNITS) { return; }

        let position: THREE.Vector3;
        if (this.world.navMesh) {
            position = this.world.navMesh.getRandomSpawnPosition();
        } else {
            position = new THREE.Vector3();
            this.world.player.getWorldPosition(position);
            position.add(new THREE.Vector3(3, 0, 0));
        }

        const spawnPoint = new CopSpawnPoint(position, this.npcSpawnService);
        spawnPoint.spawn(this.world, (cop) => {
            this.activeUnits.add(cop);
        });
    }

    public clearActiveUnits(): void {
        this.activeCopCars.forEach((car) => this.world.remove(car));
        this.activeCopCars.clear();
        this.activeUnits.clear();
    }

    public destroy(): void {
        this.subscription.unsubscribe();
    }

    private onWantedLevelChange(level: number): void {
        // this.cleanDeadUnits();

        const footTarget = this.targetFootCopCount(level);
        const carTarget = this.targetCopCarCount(level);
        const footCurrent = this.activeUnits.size;
        const carCurrent = this.activeCopCars.size;

        if (level === 0) {
            // Release all units and cars — keep them in the world.
            // CopBehaviour's wantedLevel$ subscription handles the behaviour switch.
            // this.activeUnits.clear();
            // this.activeCopCars.clear();
            return;
        }

        if (footTarget > footCurrent) {
            const toSpawn = Math.min(footTarget - footCurrent, MAX_ACTIVE_UNITS - footCurrent);
            this.spawnFootCops(toSpawn);
        }

        if (carTarget > carCurrent) {
            this.spawnCopCars(carTarget - carCurrent);
        } else if (carTarget < carCurrent) {
            // this.despawnCopCars(carCurrent - carTarget);
        }
    }

    private spawnCopCars(count: number): void {
        const positions = this.computeSpawnPositions(count);
        positions.forEach((position) => {
            const spawnPoint = new CopCarSpawnPoint(position, this.npcSpawnService);
            spawnPoint.spawn(this.world, (cop, car) => {
                this.activeUnits.add(cop);
                this.activeCopCars.add(car);
            });
        });
    }

    private despawnCopCars(count: number): void {
        const cars = [...this.activeCopCars].slice(-count);
        cars.forEach((car) => {
            const driver = car.controllingCharacter;
            if (driver) {
                this.world.remove(driver as Npc);
                this.activeUnits.delete(driver as Npc);
            }
            this.world.remove(car);
            this.activeCopCars.delete(car);
        });
    }

    private spawnFootCops(count: number): void {
        const positions = this.computeSpawnPositions(count);
        positions.forEach((position) => {
            const spawnPoint = new CopSpawnPoint(position, this.npcSpawnService);
            spawnPoint.spawn(this.world, (cop) => {
                this.activeUnits.add(cop);
            });
        });
    }

    private releaseExcessFootCops(count: number): void {
        const sorted = this.sortByDistanceFromPlayer([...this.activeUnits]);
        sorted.slice(-count).forEach((unit) => {
            this.activeUnits.delete(unit);
        });
    }

    private computeSpawnPositions(count: number): THREE.Vector3[] {
        if (!this.world.player || count === 0) { return []; }

        if (this.world.navMesh) {
            return Array.from({ length: count }, () => this.world.navMesh.getRandomSpawnPosition());
        }

        const playerPos = new THREE.Vector3();
        this.world.player.getWorldPosition(playerPos);
        const positions: THREE.Vector3[] = [];
        for (let i = 0; i < count; i++) {
            const angle = ((Math.PI * 2) / count) * i;
            positions.push(new THREE.Vector3(
                playerPos.x + Math.cos(angle) * OFFSCREEN_SPAWN_RADIUS,
                playerPos.y,
                playerPos.z + Math.sin(angle) * OFFSCREEN_SPAWN_RADIUS,
            ));
        }
        return positions;
    }

    private sortByDistanceFromPlayer(units: Npc[]): Npc[] {
        if (!this.world.player) { return units; }
        const playerPos = new THREE.Vector3();
        this.world.player.getWorldPosition(playerPos);

        return units.sort((a, b) => {
            const posA = new THREE.Vector3();
            const posB = new THREE.Vector3();
            a.getWorldPosition(posA);
            b.getWorldPosition(posB);
            return posA.distanceTo(playerPos) - posB.distanceTo(playerPos);
        });
    }

    private cleanDeadUnits(): void {
        this.activeUnits.forEach((unit) => {
            if (!this.world.characters.includes(unit)) {
                this.activeUnits.delete(unit);
            }
        });
        this.activeCopCars.forEach((car) => {
            if (!this.world.vehicles.includes(car)) {
                this.activeCopCars.delete(car);
            }
        });
    }
}
